// =============================================================
// Login Page
// =============================================================

import { useState, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/api';
import { IconCheck, IconSEOIntelligence } from '../components/icons';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Unable to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-bg-gradient" />

      {/* Left branding panel */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-icon"><IconSEOIntelligence style={{ width: 24, height: 24 }} /></div>
          <span className="auth-brand-name">Local SEO AI</span>
        </div>

        <div className="auth-tagline">
          <h1>
            Dominate local search with <span>AI-powered</span> SEO
          </h1>
          <p>
            Connect your Google Business Profile, track keywords, monitor competitors,
            and let AI generate SEO strategies that actually work.
          </p>
        </div>

        <div className="auth-features">
          {[
            { icon: <IconCheck style={{ width: 20, height: 20, color: '#10b981' }} />, text: 'Google Business Profile management' },
            { icon: <IconCheck style={{ width: 20, height: 20, color: '#10b981' }} />, text: 'Local keyword & ranking tracking' },
            { icon: <IconCheck style={{ width: 20, height: 20, color: '#10b981' }} />, text: 'Review monitoring & response drafts' },
            { icon: <IconCheck style={{ width: 20, height: 20, color: '#10b981' }} />, text: 'AI-generated SEO recommendations' },
          ].map((f) => (
            <div className="auth-feature" key={f.text}>
              <div className="auth-feature-icon">{f.icon}</div>
              <span className="auth-feature-text">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="auth-right">
        <div className="auth-form-container">
          <h2 className="auth-form-title">Welcome back</h2>
          <p className="auth-form-subtitle">Sign in to your account to continue</p>

          {error && (
            <div className="alert alert-error" role="alert">
              <span></span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="form-input"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label required">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-input"
                placeholder=""
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              id="login-submit-btn"
              type="submit"
              className={`btn btn-primary btn-full btn-lg ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="auth-form-footer">
            Don't have an account?{' '}
            <Link to="/register">Create one free</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
